import { useEffect, useState } from "react";
import moment from "moment";
import {
  useGetProviderLocationsQuery,
  useGetProvidersQuery,
  appointmentApi,
} from "../../redux/service/appointments_service";
import { getLogedPid, getLoggedinUser } from "../../userStore";
import {
  createTimeSlots,
  capitalizeFirstLetter,
  getUserPictureUrl,
} from "../../utils/common_lib";
import { Loader } from "../../components/Loader";
import {
  InputField,
  SimpleRichFiled,
} from "../../components/ReactHooksFormFields/inputField";
import { DatePickerField } from "../../components/ReactHooksFormFields/DatePickerField";
import { useForm } from "react-hook-form";
import { BELLY_HEALTH_PRACTICE_ID } from "../../constants/practiceIds";
import LocationPinIcon from "../../components/Icons/LocationPinIcon";
import LocationIcon from "../../components/Icons/LocationIcon";
import UserIcon from "../../components/Icons/UserIcon";
import CalendarIcon from "../../components/Icons/CalendarIcon";
import CalendarFilledIcon from "../../components/Icons/CalendarFilledIcon";
import LoadingIcon from "../../components/Icons/LoadingIcon";
import EmailIcon from "../../components/Icons/EmailIcon";
import TeleConsultIcon from "../../components/Icons/TeleConsultIcon";
import BackIcon from "../../components/Icons/BackIcon";
import SearchIcon from "../../components/Icons/SearchIcon";
import TickIcon from "../../components/Icons/TickIcon";
import NextIcon from "../../components/Icons/NextIcon";

const AppointmentProvider = ({
  mode = "provider",
  selectedProvider: selectedProviderProp,
  selectedLocationForBooking,
  goBack,
  saveAppointmentHandler,
  isSaving,
}) => {
  const isProviderMode = mode === "provider";

  const { data: locations, isLoading: isLoadingLocations } = useGetProviderLocationsQuery(
    { providers: getLogedPid(), user_id: selectedProviderProp?.id },
    { skip: !isProviderMode }
  );

  const { data: providers, isLoading: isLoadingProviders } = useGetProvidersQuery(
    { role: "1,44", has_calendar: 1, location_id: selectedLocationForBooking?.id },
    { skip: isProviderMode }
  );

  const isDataLoading = isProviderMode ? isLoadingLocations : isLoadingProviders;

  const user = getLoggedinUser();
  const isPracticeBellyHealth = user?.practice_id === BELLY_HEALTH_PRACTICE_ID;
  const startDate = isPracticeBellyHealth ? moment().add(3, "day").toDate() : new Date();

  const { control, setValue, watch } = useForm({
    defaultValues: {
      startDate: startDate,
      external_guest: "",
      conf_call: false,
      viaEmail: false,
    },
  });

  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedProviderLocal, setSelectedProviderLocal] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [reasonText, setReasonText] = useState("");
  const [selectTime, setSelectTime] = useState("");
  const [validationError, setValidationError] = useState(false);
  const [timeSlotsData, setTimeSlotsData] = useState({});
  const [startDateSelection, setStartDateSelection] = useState();
  const [selectedDates, setSelectedDates] = useState(null);

  const teleConsult = watch("conf_call");
  const viaEmail = watch("viaEmail");
  const external_guest = watch("external_guest");

  const activeProviderId = isProviderMode ? selectedProviderProp?.id : selectedProviderLocal?.id;
  const activeLocationId = isProviderMode ? selectedLocation : selectedLocationForBooking?.id;
  const canShowCalendar = isProviderMode || !!selectedProviderLocal;

  useEffect(() => {
    setStartDateSelection(startDate);
    setValue("startDate", startDate);
  }, []);

  useEffect(() => {
    if (isProviderMode && locations) {
      setSelectedLocation(locations?.[0]?.id);
      getSlotsForIds(selectedProviderProp?.id, locations?.[0]?.id);
    }
  }, [locations]);

  const [getTimeSlotTrigger, { isLoading: timeLoading }] =
    appointmentApi.endpoints.getTimeSlots.useLazyQuery();

  const getSlotsForIds = async (providerId, locationId, startDateParams) => {
    if (!providerId || !locationId) return;
    setLoading(true);
    let newStartDate = startDateParams || startDateSelection || startDate;
    const endDate = newStartDate ? new Date(newStartDate) : null;
    if (endDate) endDate.setMonth(endDate.getMonth() + 1);
    const { data: newTimeSlot } = await getTimeSlotTrigger({
      provider: providerId,
      location_id: locationId,
      start_date: newStartDate
        ? moment(newStartDate).format("YYYY-MM-DD")
        : new Date().toISOString(),
      end_date: endDate
        ? moment(endDate).format("YYYY-MM-DD")
        : new Date().toISOString(),
      skip_encounter: true,
    });
    const slotsByDate = {};
    if (newTimeSlot) {
      for (const [date, info] of Object.entries(newTimeSlot)) {
        slotsByDate[date] = [];
        const slots = createTimeSlots(
          info?.calendar?.[0]?.from_time,
          info?.calendar?.[0]?.to_time,
          parseInt(info.calendar?.[0]?.slot_time),
          info.blocks,
          info.calendar?.[0]?.id
        );
        slotsByDate[date].push(...slots);
      }
      setTimeSlotsData(slotsByDate);
      const allDates = Object.keys(slotsByDate);
      setSelectedDates(allDates.slice(0, Math.min(4, allDates.length)));
    }
    setLoading(false);
  };

  function incrementDateSelection() {
    if (!selectedDates || selectedDates.length === 0) return;
    const lastSelectedDate = selectedDates[selectedDates.length - 1];
    const keys = Object.keys(timeSlotsData);
    const lastSelectedIndex = keys.indexOf(lastSelectedDate);
    if (lastSelectedIndex + 1 < keys.length) {
      let newItem = keys[lastSelectedIndex + 1];
      setSelectedDates(selectedDates.slice(1).concat(newItem));
    }
  }

  function decrementDateSelection() {
    if (!selectedDates || selectedDates.length === 0) return;
    const firstSelectedDate = selectedDates[0];
    const keys = Object.keys(timeSlotsData);
    const firstSelectedIndex = keys.indexOf(firstSelectedDate);
    if (firstSelectedIndex > 0) {
      let newItem = keys[firstSelectedIndex - 1];
      setSelectedDates([newItem].concat(selectedDates.slice(0, -1)));
    }
  }

  const changeLocationSelection = (id) => {
    setSelectedLocation(id);
    setTimeSlotsData({});
    setSelectedDates([]);
    getSlotsForIds(selectedProviderProp?.id, id);
  };

  const selectProviderHandler = (provider) => {
    setSelectedProviderLocal(provider);
    setSelectTime("");
    setTimeSlotsData({});
    setSelectedDates(null);
    setValidationError(false);
    getSlotsForIds(provider?.id, selectedLocationForBooking?.id);
  };

  const filteredProviders = providers?.filter((value) => {
    const name = value?.User?.name?.toLowerCase() || "";
    const specialty = value?.User?.specialty?.toLowerCase() || "";
    const search = searchTerm.toLowerCase();
    return name.includes(search) || specialty.includes(search);
  });

  const requestAppointment = () => {
    if (selectTime == "") {
      setValidationError(true);
      return;
    }
    const useTeleConsult = isPracticeBellyHealth || teleConsult;
    saveAppointmentHandler({
      provider: activeProviderId,
      location: activeLocationId,
      selectTime: selectTime,
      reason: reasonText,
      cat_id: useTeleConsult ? 12 : 5,
      conf_call: useTeleConsult ? "yes" : "no",
      external_guest: external_guest,
    });
  };

  const handleTodayClick = () => {
    setStartDateSelection(new Date());
    getSlotsForIds(activeProviderId, activeLocationId, new Date());
    setValue("startDate", new Date());
  };

  const handleDateChange = (e) => {
    setStartDateSelection(moment(e).toDate());
    getSlotsForIds(activeProviderId, activeLocationId, moment(e).toDate());
  };

  const renderProviderModeHeader = () => (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <div className="flex gap-6 items-center">
        <div className="flex-shrink-0">
          <img
            className="h-24 w-24 rounded-full border-4 border-blue-100 shadow-lg object-cover"
            src={getUserPictureUrl(selectedProviderProp?.id, "provider")}
            alt={selectedProviderProp?.name}
          />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {selectedProviderProp?.name}
          </h2>
          <div className="flex gap-4 text-gray-600">
            <span className="flex items-center gap-1">
              <UserIcon className="w-5 h-5" />
              {capitalizeFirstLetter(selectedProviderProp?.sex)}
            </span>
            <span className="flex items-center gap-1">
              <CalendarIcon className="w-5 h-5" />
              {moment(selectedProviderProp?.DOB).format("MM/DD/YYYY")} ({selectedProviderProp?.age})
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderLocationModeHeader = () => (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <div className="flex gap-4 items-center">
        <div className="text-blue-600">
          <LocationIcon className="w-8 h-8" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {selectedLocationForBooking?.name}
          </h2>
          {selectedLocationForBooking?.address1 && (
            <p className="text-gray-600">
              {selectedLocationForBooking?.address1}
              {selectedLocationForBooking?.city && `, ${selectedLocationForBooking?.city}`}
              {selectedLocationForBooking?.state && `, ${selectedLocationForBooking?.state}`}
              {selectedLocationForBooking?.zip && ` ${selectedLocationForBooking?.zip}`}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  const renderLocationList = () => (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
        <LocationIcon className="w-6 h-6 text-blue-600" />
        Select Location
      </h2>
      <div className="space-y-3 max-h-[450px] overflow-y-auto pr-2">
        {locations?.map((location) => (
          <div
            key={location?.id}
            className={`cursor-pointer rounded-lg border-2 transition-all duration-200 ${
              selectedLocation == location?.id
                ? "border-blue-500 bg-blue-50 shadow-md"
                : "border-gray-200 hover:border-blue-300 hover:bg-gray-50"
            }`}
            onClick={() => changeLocationSelection(location?.id)}>
            <div className="p-4">
              <div className="flex justify-between items-start">
                <div className="flex gap-3 flex-1">
                  <div className={`mt-1 ${selectedLocation?.toString() === location?.id?.toString() ? "text-blue-600" : "text-gray-400"}`}>
                    <LocationPinIcon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 mb-1">{location?.name}</p>
                    <p className="text-sm text-gray-600">
                      {location?.address1}
                      {location?.address2 && `, ${location?.address2}`}
                    </p>
                    <p className="text-sm text-gray-600">
                      {location?.city}, {location?.state} {location?.zip}
                    </p>
                  </div>
                </div>
                <div className="flex-shrink-0 ml-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedLocation == location?.id ? "border-blue-600 bg-blue-600" : "border-gray-400"
                  }`}>
                    {selectedLocation == location?.id && (
                      <TickIcon className="w-3 h-3 text-white" />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderProviderList = () => (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <h2 className="text-xl font-bold text-gray-900 p-6 pb-0 flex items-center gap-2">
        <UserIcon className="w-6 h-6 text-blue-600" />
        Select Provider
      </h2>
      <div className="p-4 border-b border-gray-200">
        <div className="relative">
          <input
            type="text"
            placeholder="Search providers by name or specialty..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <SearchIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
        </div>
      </div>
      <div className="max-h-[450px] overflow-y-auto">
        {filteredProviders?.map((value, index) => (
          <div
            key={value?.User?.id || index}
            className={`cursor-pointer transition-all duration-200 border-b border-gray-200 last:border-b-0 ${
              selectedProviderLocal?.id === value?.User?.id
                ? "bg-blue-50 border-l-4 border-l-blue-500 shadow-sm"
                : "hover:bg-gray-50 border-l-4 border-l-transparent"
            }`}
            onClick={() => selectProviderHandler(value?.User)}>
            <div className="flex justify-between items-center p-4">
              <div className="flex items-center gap-3">
                <img
                  className="h-10 w-10 rounded-full border-2 border-gray-200 object-cover"
                  src={getUserPictureUrl(value?.User?.id, "provider")}
                  alt={value?.User?.name}
                />
                <div>
                  <p className="font-semibold text-gray-900">{value?.User?.name}</p>
                  {value?.User?.specialty && (
                    <p className="text-sm text-gray-600">{value?.User?.specialty}</p>
                  )}
                </div>
              </div>
              {selectedProviderLocal?.id === value?.User?.id && (
                <svg className="w-6 h-6 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
            </div>
          </div>
        ))}
        {isLoadingProviders && (
          <div className="py-8"><Loader /></div>
        )}
        {filteredProviders?.length === 0 && !isLoadingProviders && (
          <div className="py-8 text-center text-gray-500">
            <p className="text-lg">
              {searchTerm ? "No providers found matching your search" : "No providers available at this location"}
            </p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {(isSaving || isDataLoading) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8">
            <Loader />
          </div>
        </div>
      )}

      <div className="text-left bg-gray-50 min-h-screen">
        <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
          <button
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium transition-colors"
            onClick={goBack}>
            <BackIcon className="w-5 h-5" />
            {isProviderMode ? "Back to Provider List" : "Back to Appointments"}
          </button>
        </div>

        <div className="max-w-7xl mx-auto p-6">
          {isProviderMode ? renderProviderModeHeader() : renderLocationModeHeader()}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {isProviderMode ? renderLocationList() : renderProviderList()}

            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white p-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <h2 className="text-lg font-bold flex items-center gap-2">
                    <CalendarIcon className="w-6 h-6" />
                    Select Date & Time
                  </h2>
                  {canShowCalendar && (
                    <div className="flex items-center gap-2">
                      {!isPracticeBellyHealth ? (
                        <button
                          onClick={handleTodayClick}
                          type="button"
                          className="px-4 py-2 bg-white text-blue-600 rounded-lg font-medium hover:bg-blue-50 transition-colors shadow-sm">
                          Today
                        </button>
                      ) : null}
                      <div className="relative bg-blue-500 rounded-lg">
                        <div className="absolute z-10 right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                          <CalendarFilledIcon className="w-5 h-5" />
                        </div>
                        <div className="w-44">
                          <DatePickerField
                            textColor="text-white"
                            color="bg-transparent"
                            control={control}
                            name="startDate"
                            defaultValues={moment(startDate).format("MMMM d, YYYY")}
                            onChange={handleDateChange}
                            dateFormat="MMMM d, yyyy"
                            placeholder="Select Date"
                            dateTime={false}
                            minDate={startDate}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {!canShowCalendar && (
                <div className="py-16 text-center text-gray-400">
                  <CalendarIcon className="w-16 h-16 mx-auto mb-4" />
                  <p className="text-lg font-medium">Select a provider to view available slots</p>
                </div>
              )}

              {canShowCalendar && (timeLoading || loading) && (
                <div className="py-12"><Loader /></div>
              )}

              {canShowCalendar && !timeLoading && !loading && (
                <>
                  <div className="relative py-6 px-8 bg-gray-50 border-y border-gray-200">
                    <button
                      className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-gray-200 transition-colors"
                      onClick={decrementDateSelection}>
                      <BackIcon className="w-5 h-5 text-gray-600" />
                    </button>

                    <div className="grid grid-cols-4 gap-3">
                      {selectedDates?.map((date) => (
                        <button
                          key={date}
                          className={`flex flex-col items-center justify-center py-3 px-2 rounded-lg border-2 transition-all duration-200 ${
                            selectTime?.date == date
                              ? "bg-blue-500 border-blue-500 text-white shadow-lg scale-105"
                              : "bg-white border-gray-300 text-gray-700 hover:border-blue-400 hover:bg-blue-50"
                          }`}>
                          <span className="text-sm font-medium">{moment(date).format("ddd")}</span>
                          <span className="text-lg font-bold mt-1">{moment(date).format("MMM D")}</span>
                        </button>
                      ))}
                    </div>

                    <button
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-gray-200 transition-colors"
                      onClick={incrementDateSelection}>
                      <NextIcon className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>

                  <div className="max-h-[450px] overflow-y-auto bg-white">
                    {selectedDates?.every((date) => !timeSlotsData[date] || timeSlotsData[date]?.length === 0) ? (
                      <div className="py-12 text-center text-gray-500">
                        <CalendarIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-lg font-medium">No available slots</p>
                        <p className="text-sm mt-1">There are no available time slots for the selected dates. Please try different dates.</p>
                      </div>
                    ) : (
                    <div className="grid grid-cols-4">
                      {selectedDates?.map((date) => {
                        let slots = timeSlotsData[date];
                        return (
                          <div key={date} className="border-r border-gray-200 last:border-r-0 p-3">
                            <div className="space-y-2">
                              {slots?.map((slot, idx) => (
                                <button
                                  key={idx}
                                  type="button"
                                  title={slot?.reason ? slot?.reason : ""}
                                  disabled={slot?.block}
                                  onClick={() => {
                                    if (slot && !slot?.block) {
                                      setSelectTime({
                                        date: date,
                                        time: slot?.start,
                                        calender_id: slot?.calender_id,
                                      });
                                    }
                                  }}
                                  className={`w-full py-3 px-2 rounded-lg text-center border-2 transition-all duration-200 ${
                                    selectTime?.time == slot?.start && selectTime?.date == date
                                      ? "bg-blue-500 border-blue-500 text-white shadow-md"
                                      : slot?.block
                                      ? "bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed"
                                      : "bg-white border-gray-300 text-gray-700 hover:border-blue-400 hover:bg-blue-50"
                                  }`}>
                                  <p className="text-sm font-semibold">{moment(slot?.start, "HH:mm").format("hh:mm")}</p>
                                  <span className="text-xs">{moment(slot?.start, "HH:mm").format("A")}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    )}
                  </div>
                </>
              )}

              {validationError && (
                <div className="px-6 py-3 bg-red-50 border-t border-red-200">
                  <p className="text-red-600 text-sm font-medium flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                      <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-8-5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 5Zm0 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd" />
                    </svg>
                    Please select appointment date & time
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 mt-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Appointment Options</h3>
            <div className="space-y-4">
              <label className="flex items-center gap-3 cursor-pointer group">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={isPracticeBellyHealth ? true : teleConsult}
                    onChange={() => setValue("conf_call", !teleConsult)}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                    disabled={isPracticeBellyHealth}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <TeleConsultIcon className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-gray-700 group-hover:text-gray-900">
                    Teleconsult Appointment
                  </span>
                </div>
              </label>

              {(teleConsult || isPracticeBellyHealth) && (
                <div className="ml-8 space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={viaEmail}
                        onChange={() => setValue("viaEmail", !viaEmail)}
                        className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <EmailIcon className="w-5 h-5 text-blue-600" />
                      <span className="font-medium text-gray-700 group-hover:text-gray-900">
                        Send invitation via Email
                      </span>
                    </div>
                  </label>
                  {viaEmail && (
                    <div className="mt-2">
                      <InputField title="Guest Email Address" name="external_guest" control={control} />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 mt-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Reason for Appointment</h3>
            <SimpleRichFiled height={"h-32"} title={""} type={"richText"} onChange={(e) => setReasonText(e)} />
          </div>

          <div className="flex justify-end gap-4 mt-6 pb-8">
            <button
              onClick={goBack}
              className="px-6 py-3 border-2 border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors"
              type="button">
              Cancel
            </button>
            <button
              onClick={requestAppointment}
              disabled={isSaving || !canShowCalendar}
              className={`px-6 py-3 font-medium rounded-lg transition-all duration-200 ${
                isSaving || !canShowCalendar
                  ? "bg-blue-400 text-white cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg"
              }`}
              type="button">
              {isSaving ? (
                <span className="flex items-center gap-2">
                  <LoadingIcon className="animate-spin h-5 w-5" />
                  Sending Request...
                </span>
              ) : (
                "Request Appointment"
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AppointmentProvider;
